import type { Camera, Point, Rect, Viewport } from "./types";

export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 4;

export function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

export function screenToCanvas(point: Point, camera: Camera): Point {
  return { x: point.x / camera.zoom - camera.x, y: point.y / camera.zoom - camera.y };
}

export function canvasToScreen(point: Point, camera: Camera): Point {
  return { x: (point.x + camera.x) * camera.zoom, y: (point.y + camera.y) * camera.zoom };
}

export function zoomAt(camera: Camera, screenPoint: Point, nextZoom: number): Camera {
  const zoom = clampZoom(nextZoom);
  const anchor = screenToCanvas(screenPoint, camera);
  return { zoom, x: screenPoint.x / zoom - anchor.x, y: screenPoint.y / zoom - anchor.y };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

export function rectContains(rect: Rect, point: Point): boolean {
  return point.x >= rect.x && point.x <= rect.x + rect.w && point.y >= rect.y && point.y <= rect.y + rect.h;
}

export function boundsOf(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const rect of rects) {
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.w);
    maxY = Math.max(maxY, rect.y + rect.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function cameraForBounds(bounds: Rect, viewport: Viewport, padding = 80): Camera {
  const width = Math.max(bounds.w + padding * 2, 1);
  const height = Math.max(bounds.h + padding * 2, 1);
  const zoom = clampZoom(Math.min(viewport.width / width, viewport.height / height));
  return {
    zoom,
    x: -bounds.x + (viewport.width / zoom - bounds.w) / 2,
    y: -bounds.y + (viewport.height / zoom - bounds.h) / 2,
  };
}

export function snap(value: number, grid: number): number {
  return grid <= 1 ? value : Math.round(value / grid) * grid;
}
